import React, {useEffect, useState} from 'react';
import Modal from './Modal';
import {WorkspaceRow} from '../../store/selectors/workspace.selectors';
import {TranscriptionQuota} from '../../store/selectors/usage.selectors';
import {formatDurationCompact} from '../../lib/audioMetadata';
import {AlertTriangleIcon} from '../Icons';

/** Languages AssemblyAI is asked for explicitly. Auto-detect is not exposed yet. */
const LANGUAGES = [
  {code: 'en', label: 'English'},
  {code: 'es', label: 'Spanish'},
];

interface TranscribeModalProps {
  row: WorkspaceRow | null;
  quota: TranscriptionQuota;
  isStarting: boolean;
  onCancel: () => void;
  onConfirm: (params: {outputFileName: string; languageCode: string}) => void;
}

/**
 * The one place a user spends transcription time, so it is the one place that has to be explicit
 * about the cost.
 *
 * Transcription is the only metered thing in the product — the dialog states the file's length,
 * what it will cost and what is left, in that order, before the button is reachable. Conversions
 * get no such dialog on purpose: they are free and unlimited.
 *
 * **Two meters, and only one of them blocks the button.** The period allowance and the per-file cap
 * are hard: the file cannot go through until money or the calendar changes them, so Start is
 * disabled. The session budget is not — it is a moving number that refills on its own, a failed job
 * hands seconds back to it, and our copy of it is as old as the last usage read. Refusing a job the
 * server would have accepted is worse than letting the server refuse it, so the session gets an
 * amber warning and the 429 (which offers to queue the job) has the final word.
 */
const TranscribeModal: React.FC<TranscribeModalProps> = ({
  row,
  quota,
  isStarting,
  onCancel,
  onConfirm,
}) => {
  const [outputFileName, setOutputFileName] = useState('');
  const [languageCode, setLanguageCode] = useState('en');

  // Reset per file rather than per open: reopening on a different row must not inherit the last
  // row's name, and the default is derived from the audio filename.
  useEffect(() => {
    if (row) {
      setOutputFileName(row.audio.name.replace(/\.[^/.]+$/, ''));
      setLanguageCode('en');
    }
  }, [row]);

  if (!row) {
    return null;
  }

  // Prefer the last job's settled duration over the upload's probed one, same as the row does —
  // it is AssemblyAI's real measurement rather than what mediaInfo.js estimated at upload time, and
  // for a job retried after a rejection it is exactly the number that got it rejected.
  const duration = row.activeTranscription?.duration ?? row.audio.duration ?? 0;
  const {secondsRemaining, maxFileSeconds, startableSeconds, session, isKnown} = quota;
  const overBalance = isKnown && secondsRemaining !== null && duration > secondsRemaining;
  const overFileLimit = maxFileSeconds !== null && duration > maxFileSeconds;
  // Only worth saying when the session is the tighter of the two — otherwise the period warning
  // above already covers it, and two warnings about the same file read as two separate problems.
  const overSession = isKnown
    && quota.isSessionBound
    && startableSeconds !== null
    && duration > startableSeconds;
  const isRetry = row.activeTranscription?.status === 'failed'
    || row.activeTranscription?.status === 'rejected';

  const canStart = outputFileName.trim().length > 0 && !isStarting && !overBalance && !overFileLimit;

  return (
    <Modal
      isOpen
      title={isRetry ? 'Retry transcription?' : 'Start transcription?'}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-400 hover:text-gray-100
             hover:bg-gray-800 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canStart}
            onClick={() => onConfirm({outputFileName: outputFileName.trim(), languageCode})}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white
             hover:bg-indigo-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            {isStarting ? 'Starting…' : 'Start transcription'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-800 bg-gray-950/50 px-3 py-2.5 text-sm">
          <p className="text-gray-200 truncate" title={row.audio.name}>{row.audio.name}</p>
          <p className="mt-1 text-gray-400">
            This file is {duration > 0 ? formatDurationCompact(duration) : 'of unknown length'}.
            {duration > 0 && ` It will use ~${formatDurationCompact(duration)} of your transcription time.`}
          </p>
          {/* Every ceiling that applies, or none: quoting one on its own is what let a user with
              15m left believe a 6m file would go through on a plan capped at 5m per file — and the
              session does the same thing again, one meter down. */}
          {isKnown && (secondsRemaining !== null || maxFileSeconds !== null || session !== null) && (
            <p className="mt-0.5 text-gray-500">
              {secondsRemaining !== null
                && `You have ${formatDurationCompact(secondsRemaining)} left this period`}
              {session !== null && startableSeconds !== null
                && `, and can start up to ${formatDurationCompact(startableSeconds)} right now`}
              {secondsRemaining !== null && '.'}
              {maxFileSeconds !== null
                && ` Any single file is capped at ${formatDurationCompact(maxFileSeconds)}.`}
            </p>
          )}
        </div>

        {overFileLimit && maxFileSeconds !== null && (
          <Warning>
            This file is longer than the {formatDurationCompact(maxFileSeconds)} per-file
            limit on your plan. Upgrade to transcribe it.
          </Warning>
        )}

        {overBalance && !overFileLimit && secondsRemaining !== null && (
          <Warning>
            This file is longer than your remaining transcription time. You have{' '}
            {formatDurationCompact(secondsRemaining)} left. This file is {formatDurationCompact(duration)}.
          </Warning>
        )}

        {/* Amber and non-blocking: this is a wait, not a wall. Saying so here — and naming the
            queue — means the 429 that follows is the outcome the user was told to expect rather
            than a refusal out of nowhere. */}
        {overSession && !overBalance && !overFileLimit && startableSeconds !== null && (
          <Warning tone="amber">
            You can start up to {formatDurationCompact(startableSeconds)} right now and this file is{' '}
            {formatDurationCompact(duration)} — your session budget, not your plan. Start it anyway
            and you&apos;ll be offered the option to queue it until the session resets.
          </Warning>
        )}

        {!overSession && !overBalance && !overFileLimit && quota.level === 'low'
          && secondsRemaining !== null && (
          <Warning tone="amber">
            You&apos;re almost out of transcription time this period —{' '}
            {formatDurationCompact(secondsRemaining)} left.
          </Warning>
        )}

        <Field label="Language" htmlFor="transcribe-language">
          <select
            id="transcribe-language"
            value={languageCode}
            onChange={event => setLanguageCode(event.target.value)}
            disabled={isStarting}
            className={controlClass}
          >
            {LANGUAGES.map(language => (
              <option key={language.code} value={language.code}>{language.label}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Number of speakers"
          optional
          htmlFor="transcribe-speakers"
          hint="Speaker counts are not accepted by the transcription API yet — every job auto-detects."
        >
          <select id="transcribe-speakers" disabled value="auto" className={controlClass}>
            <option value="auto">Auto-detect</option>
          </select>
        </Field>

        <Field
          label="Output name"
          htmlFor="transcribe-output"
          hint="`.csv` is added for you, and a name already in use gets a number appended."
        >
          <input
            id="transcribe-output"
            type="text"
            value={outputFileName}
            onChange={event => setOutputFileName(event.target.value)}
            disabled={isStarting}
            placeholder="my-transcription"
            className={controlClass}
          />
        </Field>
      </div>
    </Modal>
  );
};

const controlClass = 'w-full rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2 text-sm '
  + 'text-gray-100 placeholder-gray-600 focus:border-indigo-500 focus:outline-none '
  + 'focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';

const Field: React.FC<{
  label: string;
  htmlFor: string;
  optional?: boolean;
  hint?: string;
  children: React.ReactNode;
}> = ({label, htmlFor, optional, hint, children}) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-gray-300">
      {label}
      {optional && <span className="ml-1 font-normal text-gray-600">(optional)</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-600">{hint}</p>}
  </div>
);

const Warning: React.FC<{children: React.ReactNode; tone?: 'red' | 'amber'}> = ({
  children,
  tone = 'red',
}) => (
  <div
    className={`flex gap-2 rounded-lg border px-3 py-2.5 text-sm ${
      tone === 'red'
        ? 'border-red-500/30 bg-red-500/[0.06] text-red-200'
        : 'border-amber-500/30 bg-amber-500/[0.06] text-amber-200'
    }`}
  >
    <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
    <p>{children}</p>
  </div>
);

export default TranscribeModal;
